import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import {
  closeTaxRule,
  deleteTaxRule,
  flattenCategories,
  listCategories,
  listTaxRules,
  type TaxRule,
  type TaxRuleFilters,
} from '@/features/inventory/api'
import { TaxRuleAlerts } from '@/features/inventory/TaxRuleAlerts'
import { TaxRuleFormDialog } from '@/features/inventory/TaxRuleFormDialog'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function scopeOf(rule: TaxRule): string {
  if (rule.product_id)
    return rule.product_code ? `${rule.product_code} — ${rule.product_name}` : 'Product'
  if (rule.category_id) return rule.category_name ?? 'Category'
  return 'Catch-all'
}

/** True while the rule has not yet taken effect -- the only state `deleteTaxRule` accepts.
 *  See `catalog.service.delete_tax_rule`. */
function notYetEffective(rule: TaxRule): boolean {
  return rule.valid_from > new Date().toISOString().slice(0, 10)
}

/** The earliest date `closeTaxRule` will accept: the API requires `valid_to > valid_from`,
 *  so today only works once the rule's window has already opened. */
function earliestCloseDate(rule: TaxRule): string {
  const today = new Date().toISOString().slice(0, 10)
  if (rule.valid_from < today) return today
  const dayAfter = new Date(`${rule.valid_from}T00:00:00Z`)
  dayAfter.setUTCDate(dayAfter.getUTCDate() + 1)
  return dayAfter.toISOString().slice(0, 10)
}

/**
 * Product- and category-level tax overrides, effective-dated. A rate and its scope are
 * fixed once created -- correcting one means closing its window and opening a new rule --
 * so this screen only ever adds, closes, or (for a rule that never took effect) removes.
 */
export function TaxRules() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<TaxRuleFilters>({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const [closing, setClosing] = useState<TaxRule | null>(null)
  const [closeDate, setCloseDate] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<TaxRule | null>(null)

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const categoryOptions = flattenCategories(categories.data ?? [])

  const { data, isLoading, error } = useQuery({
    queryKey: ['tax-rules', filters],
    queryFn: () => listTaxRules(filters),
    retry: false,
  })

  const canWrite = can('products', 'update')
  const canDelete = can('products', 'delete')

  const close = useMutation({
    mutationFn: ({ rule, validTo }: { rule: TaxRule; validTo: string }) =>
      closeTaxRule(rule.id, validTo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tax-rules'] })
      toast.success('Tax rule closed.')
      setClosing(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  const remove = useMutation({
    mutationFn: (rule: TaxRule) => deleteTaxRule(rule.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tax-rules'] })
      toast.success('Tax rule removed.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  const rules = data ?? []

  const columns: Column<TaxRule>[] = [
    { key: 'name', header: 'Name', className: 'font-medium', cell: (rule) => rule.name },
    { key: 'scope', header: 'Applies to', cell: (rule) => scopeOf(rule) },
    {
      key: 'rate',
      header: 'Rate',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      cell: (rule) => `${rule.rate}%`,
    },
    { key: 'valid_from', header: 'From', cell: (rule) => rule.valid_from },
    {
      key: 'valid_to',
      header: 'To',
      cell: (rule) =>
        rule.valid_to ?? <span className="text-slate-400 dark:text-slate-500">Open-ended</span>,
    },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: (rule: TaxRule) => (
              <div className="flex justify-end gap-2">
                {rule.valid_to === null && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setClosing(rule)
                      setCloseDate(earliestCloseDate(rule))
                    }}
                  >
                    Close
                  </Button>
                )}
                {canDelete && notYetEffective(rule) && (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(rule)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          } satisfies Column<TaxRule>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Tax rules</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {rules.length} rule{rules.length === 1 ? '' : 's'} — overrides a product's own tax rate
            for the scope and dates below.
          </p>
        </div>
        {canWrite && <Button onClick={() => setDialogOpen(true)}>Add tax rule</Button>}
      </header>

      {canWrite && <TaxRuleAlerts />}

      <Card>
        <CardHeader
          title="Product and category overrides"
          description="The most specific rule wins: a product rule beats a category rule beats the catch-all, tie-broken by the most recent start date."
        />
        <DataTable
          rows={rules}
          columns={columns}
          getRowId={(rule) => rule.id}
          caption="Tax rules"
          isLoading={isLoading}
          error={error}
          emptyMessage="No tax rules match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="tax-rule-category">
                Category
              </label>
              <select
                id="tax-rule-category"
                className={SELECT_CLASS}
                value={filters.category_id ?? ''}
                onChange={(event) =>
                  setFilters((current) => ({
                    ...current,
                    category_id: event.target.value || undefined,
                  }))
                }
              >
                <option value="">Any category</option>
                {categoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              {filters.category_id && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({})}>
                  Clear
                </Button>
              )}
            </div>
          }
        />
      </Card>

      {dialogOpen && <TaxRuleFormDialog open onClose={() => setDialogOpen(false)} />}

      <Modal
        open={closing !== null}
        onClose={() => setClosing(null)}
        title={closing ? `Close "${closing.name}"?` : 'Close tax rule'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setClosing(null)}>
              Cancel
            </Button>
            <Button
              loading={close.isPending}
              disabled={!closeDate}
              onClick={() => closing && close.mutate({ rule: closing, validTo: closeDate })}
            >
              Close rule
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert tone="info">
            Ends this rule's effective window. Orders already priced under it are unaffected;
            anything priced from this date on falls back to the next most specific rule.
          </Alert>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Effective to
            <Input
              type="date"
              value={closeDate}
              min={closing ? earliestCloseDate(closing) : undefined}
              onChange={(event) => setCloseDate(event.target.value)}
              className="mt-1"
            />
          </label>
        </div>
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : 'Delete tax rule'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          This rule has not taken effect yet, so it can be removed outright rather than closed.
        </Alert>
      </Modal>
    </div>
  )
}
